import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HiArrowNarrowLeft } from "react-icons/hi";
import logo from "@/assets/logo.png";
import { applyPosthogConsent } from "@/instrument";
import { getCookieConsent, setCookieConsent, COOKIE_CONSENT_EVENT } from "@/utils";

const Cookies = () => {
  const [consent, setConsent] = useState(getCookieConsent());

  const onChoice = (value) => {
    setCookieConsent(value);
    applyPosthogConsent(value === "accepted");
    setConsent(value);
    window.dispatchEvent(new Event(COOKIE_CONSENT_EVENT));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors">
            <HiArrowNarrowLeft className="w-5 h-5" />
            Retour
          </Link>
          <img src={logo} alt="Logo" className="h-8" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 pb-16">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Politique cookies</h1>
        <p className="text-sm text-gray-400 mb-8">Dernière mise à jour : 15 août 2026</p>

        <div className="space-y-8 text-sm text-gray-600 leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">1. De quoi parle-t-on ?</h2>
            <p>
              Un cookie est un petit fichier déposé sur votre appareil lors de la consultation d'un site. Le service utilise également le stockage local de votre
              navigateur (localStorage et sessionStorage), qui répond aux mêmes règles. Ces traceurs servent ici à deux choses : faire fonctionner votre parcours
              d'enregistrement de manière sécurisée, et mesurer l'utilisation du service pour l'améliorer.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">2. Traceurs strictement nécessaires</h2>
            <p>
              Ces traceurs sont indispensables au fonctionnement du service (authentification, sécurité du formulaire, protection contre la fraude). Ils ne nécessitent
              pas votre consentement et ne peuvent pas être désactivés sans interrompre votre enregistrement.
            </p>
            <p className="mt-2">
              Ils servent à sécuriser votre formulaire d'enregistrement en cours et à vérifier que la session se poursuit sur le même appareil (conservés quelques
              heures), à maintenir votre session authentifiée après vérification par code (valable jusqu'à 3 jours), et à reconnaître votre appareil pour prévenir la
              fraude (30 jours). Lorsque vous vérifiez votre identité par code, un jeton de reconnaissance est en outre conservé 45 jours sur votre appareil afin de vous
              reconnaître lors de vos prochaines visites.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">3. Mesure d'audience</h2>
            <p>
              Le service utilise PostHog, hébergé dans l'Union européenne, pour mesurer l'utilisation du parcours (étapes complétées, erreurs rencontrées, performance)
              et l'améliorer. PostHog dépose des traceurs (préfixés <span className="font-mono">ph_</span>, en cookie et dans le stockage local) contenant un identifiant
              d'utilisation, conservés au maximum 13 mois. Ces traceurs ne sont déposés qu'avec votre consentement, recueilli via le bandeau affiché lors de votre
              première visite ; votre choix est conservé 6 mois. Ces données ne sont pas croisées avec le contenu de votre dossier médical et ne servent à aucune fin
              publicitaire.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">4. Supervision des erreurs</h2>
            <p>
              Le service utilise Sentry pour détecter et corriger les erreurs techniques. Sentry ne dépose pas de cookie ; seules des informations techniques sur
              l'erreur rencontrée sont transmises, vers des serveurs situés dans l'Union européenne.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">5. Gérer votre choix</h2>
            <p>
              Vous pouvez à tout moment accepter ou refuser les traceurs de mesure d'audience ci-dessous. Votre choix prend effet immédiatement et vaut pour vos
              prochaines visites.
            </p>
            <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-900 font-medium">
                Mesure d'audience :{" "}
                {consent === "accepted" ? (
                  <span className="text-green-600">acceptée</span>
                ) : consent === "refused" ? (
                  <span className="text-red-500">refusée</span>
                ) : (
                  <span className="text-gray-500">aucun choix enregistré</span>
                )}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => onChoice("refused")}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Refuser
                </button>
                <button
                  onClick={() => onChoice("accepted")}
                  className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-600 transition-colors"
                >
                  Accepter
                </button>
              </div>
            </div>
            <p className="mt-3">
              Vous pouvez également supprimer les cookies et le stockage local depuis les réglages de votre navigateur, ou configurer celui-ci pour les bloquer. La
              suppression des traceurs strictement nécessaires interrompra votre enregistrement en cours ; il faudra alors le recommencer ou vous présenter à l'accueil
              du laboratoire. Aucun traceur publicitaire n'est utilisé sur ce service.
            </p>
            <p className="mt-2">
              Pour en savoir plus sur les traitements de données associés, consultez la{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                politique de confidentialité
              </Link>
              .
            </p>
          </section>
        </div>

        <div className="mt-10 pt-6 border-t border-gray-200 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link to="/terms" className="text-primary hover:underline">
            Conditions générales d'utilisation
          </Link>
          <Link to="/privacy" className="text-primary hover:underline">
            Politique de confidentialité
          </Link>
        </div>
      </main>
    </div>
  );
};

export default Cookies;
